import { Navigate } from 'react-router-dom';
import type { RouteRecord } from 'vite-react-ssg';
import RootLayout from './RootLayout';
import LandingPage from './pages/LandingPage';
import WaitlistPage from './pages/WaitlistPage';
import AppLayout from './app/AppLayout';
import DashboardPage from './app/pages/DashboardPage';
import DocumentsPage from './app/pages/DocumentsPage';
import DocumentDetailPage from './app/pages/DocumentDetailPage';
import TemplateFillPage from './app/pages/TemplateFillPage';
import AssistantPage from './app/pages/AssistantPage';
import CasesPage from './app/pages/CasesPage';
import CaseDetailPage from './app/pages/CaseDetailPage';
import EmployeesPage from './app/pages/EmployeesPage';
import EmployeeDetailPage from './app/pages/EmployeeDetailPage';
import CompanyPage from './app/pages/CompanyPage';
// LawWatchPage is parked for now: "law watch" framing risks sounding like
// legal advice and it isn't MVP — the page component stays for later.
import SettingsPage from './app/pages/SettingsPage';
import OnboardingPage from './app/pages/OnboardingPage';
import DevFixesPage from './app/pages/DevFixesPage';
import DevTemplatePage from './app/pages/DevTemplatePage';
// Clerk lives only under /app (see AppAuth.tsx). AppAuthGate wraps only the /app
// routes, so the marketing routes below stay pre-rendered and backend-independent.
import { AppAuthGate, SignInPage, SignUpPage } from './app/auth/AppAuth';

// Marketing routes are pre-rendered to static HTML at build time. Concrete
// paths ("/", "/waitlist") each produce their own index.html; the "*" fallback
// is a client-side safety net that renders the landing page for unknown paths
// so hydration matches the host's SPA fallback.
//
// The app under /app is deliberately NOT pre-rendered (see includedRoutes in
// vite.config.ts) — it's a client-side product surface running on dummy data
// until the backend exists.
export const routes: RouteRecord[] = [
  {
    path: '/',
    element: <RootLayout />,
    children: [
      { index: true, element: <LandingPage /> },
      { path: 'waitlist', element: <WaitlistPage /> },
      // DEV-ONLY: compliance-fix overlay harness (no auth/backend). See DevFixesPage.
      { path: 'dev/fixes', element: <DevFixesPage /> },
      // DEV-ONLY: template-editor harness on a static sample bundle (no auth/backend).
      { path: 'dev/template', element: <DevTemplatePage /> },
      // Clerk sign-in / sign-up. Client-only under /app (excluded from SSG), and
      // deliberately OUTSIDE AppAuthGate — these ARE the login screens.
      { path: 'app/sign-in/*', element: <SignInPage /> },
      { path: 'app/sign-up/*', element: <SignUpPage /> },
      // Full-screen onboarding lives outside the sidebar shell. Kept as an
      // explicit sibling path (not a pathless layout) because vite-react-ssg's
      // route flattening only follows `path` fields.
      { path: 'app/onboarding', element: <AppAuthGate><OnboardingPage /></AppAuthGate> },
      {
        path: 'app',
        element: (
          <AppAuthGate>
            <AppLayout />
          </AppAuthGate>
        ),
        children: [
          { index: true, element: <DashboardPage /> },
          { path: 'documents', element: <DocumentsPage /> },
          { path: 'documents/:slug', element: <DocumentDetailPage /> },
          // The template editor: fill a document type's template with a live two-pane preview.
          { path: 'documents/:slug/template', element: <TemplateFillPage /> },
          { path: 'assistant', element: <AssistantPage /> },
          { path: 'employees', element: <EmployeesPage /> },
          { path: 'employees/:employeeId', element: <EmployeeDetailPage /> },
          { path: 'cases', element: <CasesPage /> },
          { path: 'cases/:caseId', element: <CaseDetailPage /> },
          { path: 'company', element: <CompanyPage /> },
          // Portfolio is hidden in single-company mode: bounce any stray/bookmarked link back to
          // the app. The page component is kept for when portfolio-level accounts return.
          { path: 'portfolio', element: <Navigate to="/app" replace /> },
          { path: 'settings', element: <SettingsPage /> },
        ],
      },
      { path: '*', element: <LandingPage /> },
    ],
  },
];
